<script setup>

    import { thumbailForSpeaker } from '@/composables/helpers/speakers';

    const props = defineProps({
        speaker: {
            type: Object,
            required: true
        },
        hide_bio: {
            type: Boolean,
            default: false
        },
        hide_featured: {
            type: Boolean,
            default: false
        }
    })

    const bio = computed(() => {
        if (!props.speaker.bio) return '';
        if (props.speaker.bio.length <= 320) return props.speaker.bio;
        return props.speaker.bio.slice(0, 320) + '...';
    });

</script>
<template>
    <div class="speaker-card">
        <div class="inner">
            <MessImage v-if="thumbailForSpeaker(speaker)" :image="thumbailForSpeaker(speaker)" lazy class="speaker-image" :alt="`${speaker.name} profile photo`" />
            <div class="speaker-image" v-else>
                <img src="@/assets/img/speaker-default.jpg" alt="Generic person image" loading="lazy" />
            </div>
            <div class="bio">
                <div class="bio-inner">
                    <span class="featured" v-if="speaker.featured && !hide_featured">Featured</span>
                    <h3 v-html="speaker.name"></h3>
                    <p class="job-title">
                        <span v-if="speaker.job_title" v-html="speaker.job_title"></span>
                        <span v-if="speaker.company">&nbsp;@ {{ speaker.company }}</span>
                    </p>
                    <p class="description" v-if="!hide_bio && speaker.bio" v-html="bio"></p>
                </div>
            </div>
        </div>
    </div>
</template>
<style lang="scss">
    .speaker-card {
        background-color: var(--cream);
        border-radius: var(--radius);
        overflow: hidden;
        scroll-snap-align: start;

        @media(min-width: $tablet) {
            background-image: url('@/assets/img/backgrounds/bubble-cream-16-small.png');
            background-position: right bottom;
            background-repeat: no-repeat;
        }

        .inner {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            width: 100%;

            @media(min-width: $tablet) {
                flex-direction: row;
                align-items: start;
                height: 100%;
                gap: 3.4375rem;
            }

            .speaker-image {
                flex: 1 0 37.93103448%;
                height: 100%;
                position: relative;
                overflow: hidden;
                width: 15vw;
                height: 15vw;

                @media(min-width: $tablet) {
                    width: auto;
                    height: 100%;
                    max-height: 24.0625rem;
                }
                
                img {
                    width: 100%;
                    height: 100%;
                    border-radius: var(--radius);
                    object-fit: cover;
                    object-position: center;
                    transition: all 250ms ease-in-out;

                    @media(min-width: $tablet) {
                        border-radius: 0;
                    }
                }
            }

            .bio {
                display: flex;
                align-items: center;
                height: 100%;
                width: 100%;

                .bio-inner {
                    width: 100%;
                }
                
                @media(min-width: $tablet) {
                    padding: 0 3.4375rem 0 0;
                }

                .featured {
                    font-size: calc(13 / var(--base-fs) * 1rem);
                    font-weight: 500;
                    line-height: 1.25rem;
                    letter-spacing: 0.040625rem;
                    color: var(--orange-dark);
                    text-transform: uppercase;
                    margin-bottom: 0.5rem;
                    display: block;
                }

                h3 {
                    font-size: calc(28 / var(--base-fs) * 1rem);
                    font-weight: 600;
                    line-height: 2.125rem;
                    margin-bottom: 1rem;
                }

                .job-title {
                    font-size: calc(13 / var(--base-fs) * 1rem);
                    font-weight: 500;
                    letter-spacing: 0.040625rem;
                    line-height: 1.25rem;
                    text-transform: uppercase;
                    margin-bottom: 1rem;
                }

                .description {
                    color: var(--teal-gray);
                    font-size: calc(15 / var(--base-fs) * 1rem);
                    font-weight: 300;
                    line-height: 1.5625rem;
                    display: -webkit-box;
                    -webkit-line-clamp: 7;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

            }
        }
    }
</style>